import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import Input from 'aldoo-ra/Input'
import Button from 'aldoo-ra/Button'
import Typography from 'aldoo-ra/Typography'
// Removed DateFilterDropdown import - no longer filtering by date
import Panel from 'aldoo-ra/Panel'
import Badge from 'aldoo-ra/Badge'
import Tooltip from 'aldoo-ra/Tooltip'
import Checkbox from 'aldoo-ra/Checkbox'
import Modal from 'aldoo-ra/Modal'
import { showNotification } from 'aldoo-ra/Notification'
import useRealtimeLogs from './useRealtimeLogs'
import StructuredDataViewer from './StructuredDataViewer'
import { 
  DEFAULT_CHANNELS, 
  LOG_LEVELS, 
  TEST_PATTERNS, 
  UI_CONFIG,
  REALTIME_CONFIG,
  // Removed TIME_RANGES and DEFAULT_TIME_RANGE - no longer filtering by date
} from './schema'
import { 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  PlayIcon, 
  PauseIcon, 
  TrashIcon,
  ChevronDownIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  DocumentDuplicateIcon,
  SpeakerWaveIcon
} from '@heroicons/react/24/outline'

const LogsOverview = () => {
  // Use the real-time logs hook
  const {
    isConnected,
    connectionStatus,
    logs,
    channels,
    // Removed timeRange - no longer filtering by date
    serverTime,
    systemStats,
    totalCount,
    isLoading,
    error: realtimeError,
    fetchChannels,
    fetchLogs,
    clearLogs: clearServerLogs,
    updateChannel,
    subscribeToChannels,
    // Removed changeTimeRange - no longer filtering by date
    getServerTime,
    // Infinite scroll
    loadOlderLogs,
    loadNewerLogs,
    scrollToBottom,
    scrollRef,
    handleScroll,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    shouldAutoScroll
  } = useRealtimeLogs()
  
  // Local UI state
  const [searchQuery, setSearchQuery] = useState('')
  const [filterQuery, setFilterQuery] = useState('')
  const [isLoggingEnabled, setIsLoggingEnabled] = useState(true)
  
  // UI state
  const [showChannelModal, setShowChannelModal] = useState(false)
  const [userScrolledUp, setUserScrolledUp] = useState(false)
  const [expandedLogs, setExpandedLogs] = useState(new Set())
  const [copiedItems, setCopiedItems] = useState(new Set())
  const [channelSearchQuery, setChannelSearchQuery] = useState('')
  const [globalEnabledLevels, setGlobalEnabledLevels] = useState([LOG_LEVELS.INFO, LOG_LEVELS.WARN, LOG_LEVELS.ERROR])
  const [channelLevelDropdowns, setChannelLevelDropdowns] = useState({})
  const [showAllChannels, setShowAllChannels] = useState(true)
  const [containerHeight, setContainerHeight] = useState('calc(100vh - 140px)')
  const [soloedChannels, setSoloedChannels] = useState(new Set())
  const [preSoloChannelStates, setPreSoloChannelStates] = useState({})

  // Refs
  const messagePatternRef = useRef(0)
  const headerRef = useRef(null)
  const statusBarRef = useRef(null)

  // Helper functions for comma-separated search
  const parseSearchTerms = (query) => {
    return query
      .split(',')
      .map(term => term.trim().toLowerCase())
      .filter(term => term.length > 0)
  }
  
  // Convert any value to searchable string
  const valueToSearchableString = (value) => {
    if (value === null || value === undefined) return ''
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value).toLowerCase()
      } catch {
        return String(value).toLowerCase()
      }
    }
    return String(value).toLowerCase()
  }

  // Recursively extract all searchable text from structured data
  const extractSearchableText = (obj, currentPath = '') => {
    let searchableText = []
    
    if (obj === null || obj === undefined) return searchableText
    
    if (typeof obj === 'object' && !Array.isArray(obj)) {
      // Handle objects - include both keys and values
      Object.entries(obj).forEach(([key, value]) => {
        const newPath = currentPath ? `${currentPath}.${key}` : key
        
        // Add the key itself as searchable
        searchableText.push(key.toLowerCase())
        
        // Add the value as searchable
        searchableText.push(valueToSearchableString(value))
        
        // Recurse into nested objects/arrays
        if (typeof value === 'object' && value !== null) {
          searchableText.push(...extractSearchableText(value, newPath))
        }
      })
    } else if (Array.isArray(obj)) {
      // Handle arrays
      obj.forEach((item, index) => {
        const newPath = currentPath ? `${currentPath}[${index}]` : `[${index}]`
        searchableText.push(valueToSearchableString(item))
        
        if (typeof item === 'object' && item !== null) {
          searchableText.push(...extractSearchableText(item, newPath))
        }
      })
    } else {
      // Handle primitive values
      searchableText.push(valueToSearchableString(obj))
    }
    
    return searchableText
  }

  const matchesSearchTerms = (log, terms) => {
    if (terms.length === 0) return true
    
    // Create comprehensive searchable text from log
    let searchableTexts = []
    
    // Add the main message
    searchableTexts.push(log.data.toLowerCase())
    
    // Add callstack if available
    if (log.callstack) {
      searchableTexts.push(log.callstack.toLowerCase())
    }
    
    // Add structured data (metadata or structuredData)
    const structuredData = log.structuredData || log.metadata
    if (structuredData && typeof structuredData === 'object') {
      searchableTexts.push(...extractSearchableText(structuredData))
    }
    
    // Add channels
    if (log.channels && Array.isArray(log.channels)) {
      searchableTexts.push(...log.channels.map(c => c.toLowerCase()))
    }
    
    // Add level
    if (log.level) {
      searchableTexts.push(log.level.toLowerCase())
    }
    
    // Check if any search term matches any searchable text
    return terms.some(term => 
      searchableTexts.some(text => text.includes(term))
    )
  }
  
  const matchesFilterTerms = (log, terms) => {
    if (terms.length === 0) return false
    
    // Use the same comprehensive search for filter terms
    let searchableTexts = []
    
    // Add the main message
    searchableTexts.push(log.data.toLowerCase())
    
    // Add callstack if available
    if (log.callstack) {
      searchableTexts.push(log.callstack.toLowerCase())
    }
    
    // Add structured data (metadata or structuredData)
    const structuredData = log.structuredData || log.metadata
    if (structuredData && typeof structuredData === 'object') {
      searchableTexts.push(...extractSearchableText(structuredData))
    }
    
    // Add channels
    if (log.channels && Array.isArray(log.channels)) {
      searchableTexts.push(...log.channels.map(c => c.toLowerCase()))
    }
    
    // Add level
    if (log.level) {
      searchableTexts.push(log.level.toLowerCase())
    }
    
    // Check if any filter term matches any searchable text (for exclusion)
    return terms.some(term => 
      searchableTexts.some(text => text.includes(term))
    )
  }

  // Format time ago function
  const formatTimeAgo = (timestamp) => {
    const now = Date.now()
    const diffMs = now - timestamp
    
    if (diffMs < 1000) {
      return 'just now'
    } else if (diffMs < 60000) {
      const seconds = Math.floor(diffMs / 1000)
      return `${seconds}s ago`
    } else if (diffMs < 3600000) {
      const minutes = Math.floor(diffMs / 60000)
      return `${minutes}m ago`
    } else if (diffMs < 86400000) {
      const hours = Math.floor(diffMs / 3600000)
      return `${hours}h ago`
    } else {
      const days = Math.floor(diffMs / 86400000)
      return `${days}d ago`
    }
  }

  // Component to display relative time that refreshes when expanded
  const RelativeTimeDisplay = ({ timestamp }) => {
    const [relativeTime, setRelativeTime] = useState(() => formatRelativeTime(timestamp))
    
    useEffect(() => {
      // Recalculate time when component mounts (when log is expanded)
      setRelativeTime(formatRelativeTime(timestamp))
    }, [timestamp])
    
    return <span className="opacity-60">{relativeTime}</span>
  }

  // Generate test data
  const generateTestMessages = useCallback(() => {
    const pattern = TEST_PATTERNS[messagePatternRef.current]
    const channel = channels.find(c => c.id === pattern.channel)
    
    const newMessages = []
    const baseTimestamp = Date.now()
    
    for (let i = 0; i < pattern.repeat; i++) {
      newMessages.push({
        id: `${baseTimestamp}-${i}`,
        timestamp: baseTimestamp + (i * 100),
        data: pattern.message,
        channels: [pattern.channel],
        color: channel.color,
        level: pattern.level,
        callstack: pattern.callstack
      })
    }
    
    messagePatternRef.current = (messagePatternRef.current + 1) % TEST_PATTERNS.length
    return newMessages
  }, [channels])

  // Message generation simulation
  // Simple connection status notification
  useEffect(() => {
    if (realtimeError && realtimeError.trim()) {
      showNotification(`Connection error: ${realtimeError}`)
    }
  }, [realtimeError])
  
  // Simple connection status tracking
  useEffect(() => {
    if (isConnected) {
      showNotification('✅ Connected to logs server')
    }
  }, [isConnected])

  // Compact consecutive identical messages  
  const compactLogs = useCallback((rawLogs) => {
    if (rawLogs.length === 0) return []
    
    const compacted = []
    let currentGroup = null
    
    for (const log of rawLogs) {
      const isSameMessage = currentGroup && 
        currentGroup.data === log.data && 
        currentGroup.level === log.level &&
        currentGroup.callstack === log.callstack &&
        JSON.stringify([...currentGroup.channels].sort()) === JSON.stringify([...log.channels].sort())
      
      if (isSameMessage) {
        currentGroup.count += 1
        currentGroup.lastTimestamp = log.timestamp
        currentGroup.endTime = log.timestamp
      } else {
        if (currentGroup) {
          compacted.push(currentGroup)
        }
        currentGroup = {
          ...log,
          count: 1,
          startTime: log.timestamp,
          endTime: log.timestamp
        }
      }
    }
    
    if (currentGroup) {
      compacted.push(currentGroup)
    }
    
    return compacted
  }, [])

  // Calculate proper container height based on header elements
  useEffect(() => {
    const calculateContainerHeight = () => {
      if (headerRef.current && statusBarRef.current) {
        const headerHeight = headerRef.current.offsetHeight
        const statusBarHeight = statusBarRef.current.offsetHeight
        
        // Try to detect external app toolbar/navigation heights
        let appToolbarHeight = 0
        let appNavigationHeight = 0
        
        // Try to find the main app container to calculate offset
        const loggerContainer = document.querySelector('.h-screen')
        let detectedTopOffset = 'not found'
        
        if (loggerContainer) {
          const containerRect = loggerContainer.getBoundingClientRect()
          const topOffset = containerRect.top
          appToolbarHeight = topOffset // This accounts for any toolbar above this component
          detectedTopOffset = topOffset
        } else {
          // Fallback to estimated heights if we can't detect
          appToolbarHeight = 60 // Typical admin toolbar height
        }
        
        const totalHeaderHeight = headerHeight + statusBarHeight + appToolbarHeight + appNavigationHeight
        const newHeight = `calc(100vh - ${totalHeaderHeight}px)`
        
        
        setContainerHeight(newHeight)
      }
    }
    
    // Calculate after component mounts and elements are rendered
    setTimeout(calculateContainerHeight, 100)
    
    // Recalculate on window resize
    window.addEventListener('resize', calculateContainerHeight)
    return () => window.removeEventListener('resize', calculateContainerHeight)
  }, [])

  // Add scroll event listener for infinite scroll and wheel/touch events for scroll lock
  useEffect(() => {
    const scrollContainer = scrollRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll)
      scrollContainer.addEventListener('wheel', handleWheel, { passive: false })
      scrollContainer.addEventListener('touchstart', handleTouchStart, { passive: false })
      scrollContainer.addEventListener('touchmove', handleTouchMove, { passive: false })
      
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll)
        scrollContainer.removeEventListener('wheel', handleWheel)
        scrollContainer.removeEventListener('touchstart', handleTouchStart)
        scrollContainer.removeEventListener('touchmove', handleTouchMove)
      }
    }
  }, [handleScroll, handleWheel, handleTouchStart, handleTouchMove])

  // Filter and compact logs
  const processedLogs = useMemo(() => {
    // Parse search and filter terms
    const searchTerms = parseSearchTerms(searchQuery)
    const filterTerms = parseSearchTerms(filterQuery)
    
    const filtered = logs.filter(log => {
      // Check if log has enabled channels
      const enabledChannelIds = channels.filter(c => c.enabled).map(c => c.id)
      const hasEnabledChannel = log.channels && log.channels.length > 0 
        ? log.channels.some(channel => enabledChannelIds.includes(channel))
        : enabledChannelIds.length > 0 // If no channels specified, show if any channel is enabled
      
      if (!hasEnabledChannel) return false
      
      // Check global level filter
      if (!globalEnabledLevels.includes(log.level)) {
        return false
      }
      
      // Check per-channel level filter
      const channelAllowsLevel = log.channels.some(channelId => {
        const channel = channels.find(c => c.id === channelId)
        return channel && channel.enabled && channel.enabledLevels.includes(log.level)
      })
      
      if (!channelAllowsLevel) {
        return false
      }
      
      // Search filter - show logs that contain ANY of the search terms (OR logic)
      // Now searches through message, structured data, callstack, channels, and level
      if (!matchesSearchTerms(log, searchTerms)) {
        return false
      }
      
      // Exclusion filter - hide logs that contain ANY of the filter terms (OR logic)
      // Also searches through all log content including structured data
      if (matchesFilterTerms(log, filterTerms)) {
        return false
      }
      
      return true
    })
    
    return compactLogs(filtered)
  }, [logs, channels, searchQuery, filterQuery, globalEnabledLevels]) // Remove function dependencies

  // Event handlers
  const toggleChannel = (channelId) => {
    // Use the updateChannel function from the hook instead of local state
    const channel = channels.find(c => c.id === channelId)
    if (channel) {
      updateChannel(channelId, { enabled: !channel.enabled })
    }
  }

  const toggleChannelLevel = (channelId, level) => {
    const channel = channels.find(c => c.id === channelId)
    if (channel) {
      const newEnabledLevels = channel.enabledLevels.includes(level)
        ? channel.enabledLevels.filter(l => l !== level)
        : [...channel.enabledLevels, level]
      updateChannel(channelId, { enabledLevels: newEnabledLevels })
    }
  }

  const toggleGlobalLevel = (level) => {
    setGlobalEnabledLevels(prev => 
      prev.includes(level) 
        ? prev.filter(l => l !== level)
        : [...prev, level]
    )
  }

  const toggleShowAllChannels = () => {
    const newEnabled = !showAllChannels
    // Update all channels
    channels.forEach(channel => {
      updateChannel(channel.id, { enabled: newEnabled })
    })
    setShowAllChannels(newEnabled)
  }

  const toggleChannelSolo = (channelId) => {
    const newSoloedChannels = new Set(soloedChannels)
    
    if (soloedChannels.has(channelId)) {
      // Remove from solo
      newSoloedChannels.delete(channelId)
      
      // If no more solo channels, restore all previous states
      if (newSoloedChannels.size === 0) {
        Object.keys(preSoloChannelStates).forEach(id => {
          updateChannel(id, { enabled: preSoloChannelStates[id] })
        })
        setPreSoloChannelStates({})
      } else {
        // Just enable this channel while keeping others solo
        updateChannel(channelId, { enabled: false })
      }
    } else {
      // Add to solo
      if (soloedChannels.size === 0) {
        // First solo channel - save current states
        const currentStates = {}
        channels.forEach(channel => {
          currentStates[channel.id] = channel.enabled
        })
        setPreSoloChannelStates(currentStates)
        
        // Disable all channels first
        channels.forEach(channel => {
          updateChannel(channel.id, { enabled: false })
        })
      }
      
      // Add to solo and enable
      newSoloedChannels.add(channelId)
      updateChannel(channelId, { enabled: true })
    }
    
    setSoloedChannels(newSoloedChannels)
  }


  const toggleConnection = () => {
    // Connection is now managed by the realtime hook
    // This button will show connection status only
    setIsLoggingEnabled(!isLoggingEnabled)
  }

  const clearLogs = async () => {
    try {
      await clearServerLogs()
      showNotification('✅ Logs cleared successfully')
    } catch (err) {
      showNotification(`❌ Failed to clear logs: ${err.message}`)
    }
  }

  const toggleLogExpansion = (logId, event) => {
    const scrollContainer = scrollRef.current
    if (!scrollContainer) return
    
    const logElement = event.currentTarget.closest('[data-log-entry]')
    if (!logElement) return
    
    const containerRect = scrollContainer.getBoundingClientRect()
    const elementRect = logElement.getBoundingClientRect()
    const relativeTop = elementRect.top - containerRect.top
    
    setExpandedLogs(prev => {
      const newSet = new Set(prev)
      
      if (newSet.has(logId)) {
        newSet.delete(logId)
      } else {
        newSet.add(logId)
      }
      
      // Maintain scroll position after DOM update
      requestAnimationFrame(() => {
        if (scrollContainer && logElement) {
          const newElementRect = logElement.getBoundingClientRect()
          const newContainerRect = scrollContainer.getBoundingClientRect()
          const currentRelativeTop = newElementRect.top - newContainerRect.top
          
          const scrollAdjustment = currentRelativeTop - relativeTop
          if (Math.abs(scrollAdjustment) > 1) {
            scrollContainer.scrollTop += scrollAdjustment
          }
        }
      })
      
      return newSet
    })
  }

  const copyToClipboard = async (text, itemId, event) => {
    event.stopPropagation()
    
    try {
      await navigator.clipboard.writeText(text)
      setCopiedItems(prev => new Set(prev).add(itemId))
      
      // Show success notification
      const contentType = itemId.includes('message') ? 'Log message' : 'Callstack'
      showNotification({
        type: 'success',
        title: 'Copied!',
        message: `${contentType} copied to clipboard`,
        duration: 2000
      })
      
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev)
          newSet.delete(itemId)
          return newSet
        })
      }, 1500)
    } catch (err) {
      console.error('Failed to copy text: ', err)
      showNotification({
        type: 'error',
        title: 'Copy Failed',
        message: 'Unable to copy to clipboard',
        duration: 3000
      })
    }
  }

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString()
  }

  const formatRelativeTime = (timestamp) => {
    const now = Date.now()
    const diff = now - timestamp
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ago`
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ago`
    } else if (minutes > 0) {
      return `${minutes} min ago`
    } else if (seconds > 0) {
      return `${seconds}s ago`
    } else {
      return 'just now'
    }
  }

  const getChannelInfo = (channelId) => {
    return channels.find(c => c.id === channelId) || { color: '#666', name: channelId }
  }

  const getLevelRibbonColor = (level) => {
    switch (level) {
      case LOG_LEVELS.WARN: return '#FFA500'
      case LOG_LEVELS.ERROR: return '#FF4444'
      case LOG_LEVELS.INFO:
      default: return 'transparent'
    }
  }

  const enabledChannelCount = channels.filter(c => c.enabled).length
  const filteredChannels = channels.filter(channel => 
    channel.name.toLowerCase().includes(channelSearchQuery.toLowerCase())
  )

  return (
    <div className="h-screen bg-admin_canvas text-admin_text flex flex-col">
      {/* Single Header Bar with All Controls */}
      <div ref={headerRef} className="flex-shrink-0 bg-admin_paper border-b border-admin_bordered p-4">
        <div className="flex items-center gap-4">
          {/* Title - Left Side */}
          <div className="flex flex-col">
            <h1 className="text-xl font-bold text-admin_text">Aldoo Logger</h1>
            <div className="text-xs text-admin_text_secondary">
              Server Time: {serverTime ? new Date(serverTime).toLocaleString() : 'N/A'}
            </div>
          </div>
          
          {/* Search Controls - Center */}
          <div className="flex items-center space-x-3 flex-1">
            <div className="flex-1 max-w-xs">
              <Input
                variant="admin_compact"
                placeholder={UI_CONFIG.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                showClearButton={true}
                debounce={300}
              />
            </div>

            <div className="flex-1 max-w-xs">
              <Input
                variant="admin_compact"
                placeholder={UI_CONFIG.filterPlaceholder}
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                leftIcon={<FunnelIcon className="w-4 h-4" />}
                showClearButton={true}
                debounce={300}
              />
            </div>

            {/* Removed Date Filter Dropdown - no longer filtering by date */}

            {/* Channels Modal Trigger */}
            <div className="relative">
              <Button
                variant="admin_secondary"
                size="sm"
                onClick={() => setShowChannelModal(true)}
                className="flex items-center space-x-2"
              >
                <span>Channels ({enabledChannelCount}/{channels.length})</span>
                <ChevronDownIcon className="w-4 h-4" />
              </Button>
              
            </div>
          </div>
          
          {/* Clear Button and Connection Toggle - Right Side */}
          <div className="flex items-center space-x-2">
            <Tooltip content="Clear all log messages">
              <Button
                variant="admin_icon"
                onClick={clearLogs}
                className="text-admin_text_secondary p-2"
              >
                <TrashIcon className="w-5 h-5" />
              </Button>
            </Tooltip>
            
            <Tooltip content={`${isLoggingEnabled ? 'Pause' : 'Resume'} log subscription`}>
              <Button
                variant="admin_primary"
                onClick={toggleConnection}
                className={`flex items-center space-x-2 w-32 justify-center border transition-all duration-600 ${
                  isConnected ? (isLoggingEnabled ? 'bg-admin_success/10 border-admin_success/30 hover:bg-admin_success/20' : 'bg-admin_progress/10 border-admin_progress/30 hover:bg-admin_progress/20') : 
                  connectionStatus === 'connecting' ? 'bg-admin_progress/10 border-admin_progress/30' : 
                  'bg-admin_error/10 border-admin_error/30'
                }`}
              >
              {/* The dot indicator */}
              <div className={`w-4 h-4 rounded-full flex-shrink-0 transition-all duration-600 ${
                isConnected ? (isLoggingEnabled ? 'bg-admin_success' : 'bg-admin_progress') : 
                connectionStatus === 'connecting' ? 'bg-admin_progress animate-pulse' : 
                'bg-admin_error'
              }`}></div>
              {/* The label */}
              <span className={`transition-colors duration-600 ${
                isConnected ? (isLoggingEnabled ? 'text-admin_success' : 'text-admin_warning') :
                connectionStatus === 'connecting' ? 'text-admin_progress' :
                'text-admin_error'
              }`}>
                {connectionStatus === 'connecting' ? 'Connecting...' :
                 isConnected ? (isLoggingEnabled ? 'Active' : 'Paused') : 'Disconnected'}
              </span>
              </Button>
            </Tooltip>
            
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div ref={statusBarRef} className="flex-shrink-0 bg-admin_paper border-b border-admin_bordered px-4 py-2">
        <div className="flex items-center justify-between text-xs text-admin_text_secondary">
          <div className="flex items-center space-x-4">
            <span>Total: {totalCount}</span>
            <span>Loaded: {logs.length}</span>
            <span>Visible: {processedLogs.length}</span>
            <span>Memory: {systemStats?.formattedMemory || '0 B'}</span>
            {(searchQuery || filterQuery) && (
              <span className="text-admin_text">Filtered: {logs.length - processedLogs.length}</span>
            )}
            {isLoading && (
              <span className="text-admin_text">Loading...</span>
            )}
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-admin_info">
              Info: {logs.filter(log => log.level === 'info').length}
            </span>

            <span className="text-admin_warning">
              Warn: {logs.filter(log => log.level === 'warn').length}
            </span>

            <span className="text-admin_error">
              Error: {logs.filter(log => log.level === 'error').length}
            </span>
          </div>
        </div>
      </div>

      {/* Log Container - Takes remaining screen height */}
      <div className="overflow-hidden relative" style={{ height: containerHeight }}>
        {/* Floating Scroll to Bottom Button */}
        {!shouldAutoScroll && (
          <div className="absolute top-4 right-9 z-10">
            <Tooltip content="Scroll to bottom">
              <Button
                variant="admin_primary"
                size="sm"
                onClick={scrollToBottom}
                className="w-10 h-10 rounded-full p-0 shadow-lg flex items-center justify-center"
              >
                ↓
              </Button>
            </Tooltip>
          </div>
        )}
        
        <div
          ref={scrollRef}
          className="h-full overflow-y-auto bg-admin_canvas p-4 font-mono text-sm"
        >
          {processedLogs.length === 0 ? (
            <div className="flex items-center justify-center h-full text-admin_text_secondary">
              <div className="text-center">
                <div className="text-lg mb-2">
                  No logs to display
                </div>

                <div className="text-sm">
                  {!isConnected ? 'Click "Start" to begin receiving logs' : 
                   !isLoggingEnabled ? 'Enable logging to see messages' :
                   'Waiting for log messages...'}
                </div>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-admin_bordered">
              {processedLogs.map(log => {
                const isExpanded = expandedLogs.has(log.id)
                return (
                  <div key={log.id} className="hover:bg-admin_neutral_1" data-log-entry>
                    <div 
                      className="flex cursor-pointer transition-colors"
                      onClick={(e) => toggleLogExpansion(log.id, e)}
                      title="Click to expand/collapse callstack"
                    >
                      {/* Left Info Panel */}
                      <div className="w-44 bg-admin_paper p-2 border-l-4 border-r-4 flex flex-col justify-between min-h-full relative" 
                           style={{ 
                             borderLeftColor: getChannelInfo(log.channels[0]).color,
                             borderRightColor: getLevelRibbonColor(log.level)
                           }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1">
                            {log.channels.map(channelId => {
                              const channelInfo = getChannelInfo(channelId)
                              return (
                                <span key={channelId} className="text-xs font-medium" style={{ color: channelInfo.color }}>
                                  {channelInfo.name}
                                </span>
                              )
                            })}
                          </div>

                          <div className="flex items-center space-x-2">
                            {log.count > 1 && (
                              <Badge variant="admin_neutral">
                                ×{log.count}
                              </Badge>
                            )}

                            <span className="text-admin_text_secondary text-xs">
                              {isExpanded ? '▼' : '▶'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-admin_text_secondary text-xs mt-1">
                          <span>{formatTimestamp(log.count > 1 ? log.endTime : log.startTime)}</span>
                          {isExpanded && (
                            <RelativeTimeDisplay timestamp={log.count > 1 ? log.endTime : log.startTime} />
                          )}
                        </div>
                      </div>
                      
                      {/* Message Text */}
                      <div className="flex-1 text-admin_text px-4 py-2 leading-relaxed relative group">
                        <div className={`pr-12 ${!isExpanded ? 'line-clamp-2' : 'whitespace-pre-wrap'}`}>
                          {log.data}
                        </div>

                        {isExpanded && (
                          <Button
                            variant="admin_icon"
                            onClick={(e) => copyToClipboard(log.data, `${log.id}-message`, e)}
                            className="absolute top-2 right-2 text-admin_neutral_3 opacity-0 group-hover:opacity-100 transition p-3"
                            title="Copy message"
                          >
                            {copiedItems.has(`${log.id}-message`) ? (
                              <span className="text-admin_success text-base">✓</span>
                            ) : (
                              <DocumentDuplicateIcon className="w-5 h-5" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Expandable Details: Structured Data + Callstack */}
                    {isExpanded && (
                      <div className="bg-admin_neutral border-t border-admin_bordered">
                        {/* Structured Data Section */}
                        {((log.structuredData && Object.keys(log.structuredData).length > 0) || (log.metadata && Object.keys(log.metadata).length > 0)) && (
                          <div className="flex border-b border-admin_bordered">
                            <div className="flex flex-col justify-between w-44 min-h-full bg-admin_paper p-2">
                              <span className="text-xs text-admin_text_secondary">
                                Context Data:
                              </span>

                              <div className="mt-2">
                                <div className="text-xs text-admin_text_secondary opacity-60">
                                  {Object.keys(log.structuredData || log.metadata || {}).length} field{Object.keys(log.structuredData || log.metadata || {}).length !== 1 ? 's' : ''}
                                </div>
                              </div>
                            </div>

                            <div className="flex-1 px-4 py-3">
                              <StructuredDataViewer 
                                data={log.structuredData || log.metadata}
                                maxDepth={4}
                                initialExpandedDepth={2}
                                onCopy={(text, itemId) => {
                                  showNotification({
                                    type: 'success',
                                    title: 'Copied!',
                                    message: 'Context data copied to clipboard',
                                    duration: 2000
                                  })
                                }}
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* Callstack Section */}
                        {log.callstack && (
                          <div className="flex">
                            <div className="w-44 bg-admin_paper p-2 flex flex-col justify-between min-h-full">
                              <span className="text-xs text-admin_text_secondary">
                                Callstack:
                              </span>

                              <div className="mt-2">
                                <div className="text-xs text-admin_text_secondary opacity-60">
                                  {log.timeSinceLastCall ? `Time: ${log.timeSinceLastCall}` : 'Time: N/A'}
                                </div>
                              </div>
                            </div>

                            <div className="flex-1 px-4 py-3 relative group">
                              <div className="text-xs text-admin_text font-mono overflow-x-auto pr-12">
                                {(() => {
                                  if (!log.callstack) return null;
                                  const lines = log.callstack.split('\n');
                                  const [firstLine, ...subsequentLines] = lines;
                                  
                                  return (
                                    <div>
                                      <div className="whitespace-pre-wrap">
                                        {firstLine}
                                      </div>

                                      {subsequentLines.map((line, index) => (
                                        <div 
                                          key={index} 
                                          className="whitespace-pre-wrap opacity-60"
                                        >
                                          {line}
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </div>

                              <Button
                                variant="admin_icon"
                                onClick={(e) => copyToClipboard(log.callstack, `${log.id}-callstack`, e)}
                                className="absolute top-2 right-2 text-admin_neutral_3 opacity-0 group-hover:opacity-100 transition p-3"
                                title="Copy callstack"
                              >
                                {copiedItems.has(`${log.id}-callstack`) ? (
                                  <span className="text-admin_success text-base">
                                    ✓
                                  </span>
                                ) : (
                                  <DocumentDuplicateIcon className="w-5 h-5" />
                                )}
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* Show message if no expandable content */}
                        {!log.structuredData && !log.metadata && !log.callstack && (
                          <div className="flex">
                            <div className="w-44 bg-admin_paper p-2">
                              <span className="text-xs text-admin_text_secondary">
                                Details:
                              </span>
                            </div>

                            <div className="flex-1 px-4 py-3 text-admin_text_secondary italic text-sm">
                              No additional context data or callstack available
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Channels Modal */}
      <Modal
        isOpen={showChannelModal}
        onClose={() => setShowChannelModal(false)}
        variant="admin_drawer_right"
        closeOnClickOutside={true}
        passInteractionEvents={true}
        title="Channel Configuration"
      >
        {/* Fixed Header Container */}
        <div className="flex-shrink-0 bg-admin_paper">
          {/* Panel Header with Global Level Filter and Show/Hide All */}
          <div className="border-b border-admin_bordered bg-admin_paper p-4">
            <div className="flex items-center justify-between mb-3">
              <Typography 
                component="h2"
                variant="admin_h4"
                className=""
              >
                Global Level Filter
              </Typography>

              <Tooltip content={showAllChannels ? "Hide all channels" : "Show all channels"}>
                <Button
                  onClick={toggleShowAllChannels}
                  variant="admin_icon"
                  className="text-admin_text_secondary"
                >
                  {showAllChannels ? (
                    <EyeIcon className="w-7 h-7" />
                  ) : (
                    <EyeSlashIcon className="w-7 h-7" />
                  )}
                </Button>
              </Tooltip>
            </div>
            
            <div className="flex justify-center">
              <div className="flex space-x-2">
                {Object.values(LOG_LEVELS).map(level => {
                  const isEnabled = globalEnabledLevels.includes(level)
                  const chipColors = {
                    [LOG_LEVELS.INFO]: isEnabled ? 'bg-admin_info text-white' : 'bg-admin_neutral_1 text-admin_text_secondary hover:bg-admin_neutral_2',
                    [LOG_LEVELS.WARN]: isEnabled ? 'bg-admin_warning text-white' : 'bg-admin_neutral_1 text-admin_text_secondary hover:bg-admin_neutral_2',
                    [LOG_LEVELS.ERROR]: isEnabled ? 'bg-admin_error text-white' : 'bg-admin_neutral_1 text-admin_text_secondary hover:bg-admin_neutral_2'
                  }
                  
                  return (
                    <Tooltip
                      key={level}
                      content={`${isEnabled ? 'Hide' : 'Show'} ${level.toUpperCase()} level messages across all channels`}
                    >
                      <button
                        onClick={() => toggleGlobalLevel(level)}
                        className={`w-8 h-8 rounded-full text-xs font-medium transition-colors cursor-pointer flex items-center justify-center ${chipColors[level]}`}
                      >
                        {level.charAt(0).toUpperCase()}
                      </button>
                    </Tooltip>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Channel Search */}
          <div className="p-4 border-b border-admin_bordered bg-admin_paper">
            <Input
              variant="admin_compact"
              placeholder={UI_CONFIG.channelSearchPlaceholder}
              value={channelSearchQuery}
              onChange={(e) => setChannelSearchQuery(e.target.value)}
              leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
              showClearButton={true}
              autoFocus={true}
            />
          </div>
        </div>

        {/* Scrollable Channels Container */}
        <div className="flex-1 overflow-hidden">
          <div className="max-h-[calc(100vh-200px)] overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper pb-20">
            {filteredChannels.length === 0 ? (
              <div className="p-4 text-center text-sm text-admin_text_secondary">
                {channelSearchQuery ? 'No channels found' : 'No channels available'}
              </div>
            ) : (
              <div className="divide-y divide-admin_bordered">
                {filteredChannels.map(channel => (
                <div 
                  key={channel.id} 
                  className={`p-4 hover:bg-admin_neutral_1 transition-all ${
                    !channel.enabled ? 'opacity-50 bg-admin_neutral_1/30' : ''
                  }`}
                >
                  {/* Channel Info Row */}
                  <div className="flex items-center justify-between">
                    {/* Left: Controls and Channel Info */}
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      {/* Solo Toggle */}
                      <Tooltip content={soloedChannels.has(channel.id) ? `Remove ${channel.name} from solo` : `Add ${channel.name} to solo`}>
                        <button
                          onClick={() => toggleChannelSolo(channel.id)}
                          className={`flex items-center justify-center w-7 h-7 rounded-full transition-colors ${
                            soloedChannels.has(channel.id)
                              ? 'text-white bg-admin_info/80 hover:bg-admin_info' 
                              : 'text-admin_text_secondary hover:text-admin_primary'
                          }`}
                        >
                          <SpeakerWaveIcon className="w-5 h-5" />
                        </button>
                      </Tooltip>
                      
                      {/* Eye Toggle */}
                      <Tooltip content={`${channel.enabled ? 'Hide' : 'Show'} all messages from ${channel.name} channel`}>
                        <button
                          onClick={() => toggleChannel(channel.id)}
                          className="flex items-center justify-center w-7 h-7 text-admin_text_secondary hover:text-admin_primary transition-colors"
                        >
                          {channel.enabled ? (
                            <EyeIcon className="w-5 h-5" />
                          ) : (
                            <EyeSlashIcon className="w-5 h-5" />
                          )}
                        </button>
                      </Tooltip>
                      
                      <div
                        className={`w-4 h-4 rounded-full flex-shrink-0 ${
                          !channel.enabled ? 'opacity-40' : ''
                        }`}
                        style={{ backgroundColor: channel.color }}
                      ></div>
                      
                      <div className={`text-base font-medium truncate ${
                        !channel.enabled ? 'text-admin_text_secondary' : 'text-admin_text'
                      }`}>
                        {channel.name}
                      </div>
                    </div>
                    
                    {/* Right: Controls and Stats */}
                    <div className="flex items-center space-x-4 flex-shrink-0">
                      {/* Stats */}
                      <div className={`text-sm text-right ${
                        !channel.enabled ? 'text-admin_text_secondary opacity-60' : 'text-admin_text_secondary'
                      }`}>
                        <div>{channel.messageCount} msgs</div>
                        <div>{(channel.messagesPerMinute || 0).toLocaleString()}/min</div>
                      </div>
                      
                      {/* Level Chips */}
                      <div className="flex space-x-1">
                        {Object.values(LOG_LEVELS).map(level => {
                          const isEnabled = channel.enabledLevels.includes(level)
                          const chipColors = {
                            [LOG_LEVELS.INFO]: isEnabled ? 'bg-admin_info text-white' : 'bg-admin_neutral_1 text-admin_text_secondary hover:bg-admin_neutral_2',
                            [LOG_LEVELS.WARN]: isEnabled ? 'bg-admin_warning text-white' : 'bg-admin_neutral_1 text-admin_text_secondary hover:bg-admin_neutral_2',
                            [LOG_LEVELS.ERROR]: isEnabled ? 'bg-admin_error text-white' : 'bg-admin_neutral_1 text-admin_text_secondary hover:bg-admin_neutral_2'
                          }
                          
                          return (
                            <Tooltip
                              key={level}
                              content={`${isEnabled ? 'Hide' : 'Show'} ${level.toUpperCase()} messages from ${channel.name} channel`}
                            >
                              <button
                                onClick={() => toggleChannelLevel(channel.id, level)}
                                className={`w-7 h-7 rounded-full text-sm font-medium transition-colors cursor-pointer flex items-center justify-center ${chipColors[level]}`}
                              >
                                {level.charAt(0).toUpperCase()}
                              </button>
                            </Tooltip>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default LogsOverview